"use client";

import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import { useTranslations } from "next-intl";
import { useMemo, type ReactNode } from "react";

type NavbarItem = {
  href: string;
  label: string;
  icon: ReactNode;
  isActive: boolean;
};

export const NAVBAR_ICONS = {
  dashboard: <DashboardOutlinedIcon fontSize="small" />,
  accommodation: <HotelOutlinedIcon fontSize="small" />,
  services: <MenuBookOutlinedIcon fontSize="small" />,
  calendar: <CalendarMonthOutlinedIcon fontSize="small" />,
  invoices: <ReceiptLongOutlinedIcon fontSize="small" />,
  customers: <SupervisorAccountIcon fontSize="small" />,
  admin: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
};

function isAccommodationPath(pathname: string) {
  return (
    pathname === "/pension" ||
    pathname.startsWith("/pension/") ||
    pathname === "/bookings" ||
    pathname.startsWith("/bookings/")
  );
}

function isServicesPath(pathname: string) {
  return (
    pathname === "/service" ||
    pathname.startsWith("/service/") ||
    pathname === "/service-offers" ||
    pathname.startsWith("/service-offers/") ||
    pathname === "/service-series" ||
    pathname.startsWith("/service-series/") ||
    pathname === "/service-bookings" ||
    pathname.startsWith("/service-bookings/") ||
    pathname === "/service-events" ||
    pathname.startsWith("/service-events/")
  );
}

export function useNavbarItems(currentPath: string, hasServiceArea: boolean) {
  const tTabs = useTranslations("tabs");

  const navItems = useMemo<NavbarItem[]>(() => {
    const items: NavbarItem[] = [
      {
        href: "/dashboard",
        label: tTabs("dashboard"),
        icon: NAVBAR_ICONS.dashboard,
        isActive:
          currentPath === "/dashboard" || currentPath.startsWith("/dashboard/"),
      },
      {
        href: "/bookings",
        label: tTabs("boarding"),
        icon: NAVBAR_ICONS.accommodation,
        isActive: isAccommodationPath(currentPath),
      },
    ];

    if (hasServiceArea) {
      items.push({
        href: "/service-offers",
        label: tTabs("services"),
        icon: NAVBAR_ICONS.services,
        isActive: isServicesPath(currentPath),
      });
    }

    items.push(
      {
        href: "/calendar",
        label: tTabs("calendar"),
        icon: NAVBAR_ICONS.calendar,
        isActive:
          currentPath === "/calendar" || currentPath.startsWith("/calendar/"),
      },
      {
        href: "/invoices",
        label: tTabs("invoices"),
        icon: NAVBAR_ICONS.invoices,
        isActive:
          currentPath === "/invoices" || currentPath.startsWith("/invoices/"),
      },
      {
        href: "/customers",
        label: tTabs("customers"),
        icon: NAVBAR_ICONS.customers,
        isActive:
          currentPath === "/customers" || currentPath.startsWith("/customers/"),
      }
    );

    return items;
  }, [currentPath, hasServiceArea, tTabs]);

  const activeAreaLabel = useMemo(() => {
    const match = navItems.find((item) => item.isActive);
    if (match) return match.label;
    if (currentPath === "/admin") return tTabs("admin");
    return "";
  }, [currentPath, navItems, tTabs]);

  return { navItems, activeAreaLabel, tTabs };
}
